import './styles.scss';

import { createNotification, handleError } from '@/utils/custom';
import { useEffect, useMemo } from 'react';

import { LoadingBlur } from '@/components/LoadingSpinner';
import PageInfo from '@/components/PageInfo';
import QuestionFilters from './components/QuestionFilters';
import QuestionModal from './components/QuestionModal';
import { QuestionsHeader } from './components/QuestionsHeader';
import { QuestionsTable } from './components/QuestionsTable';
import { QuestionsTabs } from './components/QuestionsTabs';
import { Toast } from '@/models';
import { TrainingQuestionsTypes } from './types';
import { createPersistentComponentState } from '@/state';
import { customIndexedDBStorage } from '@/state/storage';
import { deleteQuestion } from '@/features/Settings/api/services/training/deleteQuestionnaireQuestions';
import useConfirmation from '@/hooks/useConfirmation';

// The Dispatch role's displayName from the backend is "Dispatch", but the tab should read "Dispatcher".
const TRAINING_ROLES = [
  { name: 'Driver', label: 'Driver' },
  { name: 'Leader', label: 'Leader' },
  { name: 'Dispatch', label: 'Dispatcher' },
];

export const useQuestionsState = createPersistentComponentState<TrainingQuestionsTypes.State>(
  'TrainingQuestionsState',
  TrainingQuestionsTypes.initState,
  { storage: customIndexedDBStorage }
);

const TrainingQuestions = () => {
  const [
    {
      questions,
      categories,
      answerTypes,
      roles,
      total,
      loading,
      roleId,
      categoryId,
      modal,
      fetch,
      fetchRoles,
      fetchCategories,
      fetchAnswerTypes,
      onSelectRole,
    },
    setState,
  ] = useQuestionsState(({ state, setState }) => [
    {
      questions: state.questions,
      categories: state.categories,
      answerTypes: state.answerTypes,
      roles: state.roles,
      total: state.total,
      loading: state.loading,
      roleId: state.filters.roleId,
      categoryId: state.filters.categoryId,
      modal: state.modals.questionModal,
      fetch: state.fetch,
      fetchRoles: state.fetchRoles,
      fetchCategories: state.fetchCategories,
      fetchAnswerTypes: state.fetchAnswerTypes,
      onSelectRole: state.onSelectRole,
    },
    setState,
  ]);
  const confirmDeleteQuestion = useConfirmation({ Footer: { resolve: 'DELETE PERMANENTLY', reject: 'CANCEL' } });

  useEffect(() => {
    fetchRoles();
    fetchCategories();
    fetchAnswerTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = useMemo(
    () =>
      TRAINING_ROLES.map(({ name, label }) => {
        const role = roles.find((role) => role.displayName.toLowerCase() === name.toLowerCase());
        return role ? { ...role, displayName: label } : undefined;
      }).filter(Boolean),
    [roles]
  );

  // Always (re)select on mount - even if a role is already cached from a previous session - so the
  // search actually runs against the live API instead of showing whatever was last persisted.
  // Categories must be loaded first since fetch() derives the role's category scope from them.
  useEffect(() => {
    if (tabs.length === 0 || categories.length === 0) return;
    const isValidRole = roleId && tabs.some((tab) => tab.id === roleId);
    onSelectRole(isValidRole ? roleId : tabs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, categories]);

  const categoriesForActiveRole = useMemo(() => categories.filter((category) => category.roleId === roleId), [categories, roleId]);

  // Only carry the active category filter into the Create form if it actually belongs to the
  // active role's categories - the filter itself is global and may point at another role's category.
  const defaultCategoryId = useMemo(
    () => (categoriesForActiveRole.some((category) => category.id === categoryId) ? categoryId : undefined),
    [categoriesForActiveRole, categoryId]
  );

  const openCreateModal = (): void => setState((current) => ({ ...current, modals: { ...current.modals, questionModal: { show: true } } }));
  const openEditModal = (id: string): void =>
    setState((current) => ({
      ...current,
      modals: { ...current.modals, questionModal: { show: true, question: questions.find((q) => q.id === id) } },
    }));
  const hideModal = (): void =>
    setState((current) => ({
      ...current,
      modals: { ...current.modals, questionModal: { ...current.modals.questionModal, show: false } },
    }));

  const handleDeleteQuestion = async (id: string): Promise<void> => {
    try {
      await confirmDeleteQuestion({
        Body: {
          title: 'Delete Training Item',
          message: 'Are you sure you want to delete this training item? It will no longer be available for new evaluations.',
        },
      });
    } catch {
      return;
    }
    try {
      await deleteQuestion([id]);
      createNotification('Success', Toast.Type.SUCCESS, 'Delete Training Item');
      fetch();
    } catch (err) {
      handleError(err, { notification: { title: 'Delete Training Item' } });
    }
  };

  return (
    <div className="TrainingQuestions">
      <QuestionFilters />
      <PageInfo>Items: {total}</PageInfo>
      <LoadingBlur loading={loading} />
      <QuestionsHeader onClick={openCreateModal} />
      <QuestionsTabs tabs={tabs} selectedRoleId={roleId} onSelect={onSelectRole} />
      <QuestionsTable categories={categories} questions={questions} onEdit={openEditModal} onDelete={handleDeleteQuestion} />
      <QuestionModal
        show={modal?.show || false}
        question={modal?.question}
        categories={categoriesForActiveRole}
        answerTypes={answerTypes}
        defaultCategoryId={defaultCategoryId}
        onHide={hideModal}
        onSubmit={fetch}
      />
    </div>
  );
};

export default TrainingQuestions;
